import { useEffect, useState } from "react";
import { NAV_LINKS, LOGO_URL } from "../data/content";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-stone-200/70 py-4"
          : "bg-transparent py-6"
      }`}
    >
      {/* SVG filter that removes near-white pixels while preserving colored logo */}
      <svg aria-hidden="true" width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <filter id="varnella-remove-white" x="0%" y="0%" width="100%" height="100%">
            <feColorMatrix
              in="SourceGraphic"
              type="luminanceToAlpha"
              result="luma"
            />
            <feComponentTransfer in="luma" result="mask">
              <feFuncA type="table" tableValues="1 1 1 1 1 1 1 0.9 0.4 0" />
            </feComponentTransfer>
            <feComposite in="SourceGraphic" in2="mask" operator="in" />
          </filter>
        </defs>
      </svg>

      <div className="max-w-[90rem] mx-auto px-6 md:px-12 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center"
          aria-label="VARNELLA — Designed for your space and no one else's"
        >
          <img
            src={LOGO_URL}
            alt="VARNELLA — Designed for your space and no one else's"
            draggable={false}
            style={{
              imageRendering: "-webkit-optimize-contrast",
              filter: "url(#varnella-remove-white)",
            }}
            className={`transition-all duration-500 object-contain select-none w-auto ${
              scrolled ? "h-14 md:h-16" : "h-16 md:h-20"
            }`}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="link-underline text-[13px] tracking-[0.2em] uppercase text-stone-700 hover:text-stone-900 transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href="#contact"
            data-testid="nav-cta-consult"
            className="btn-primary text-[11px]"
          >
            Request Consultation
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-stone-900"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-stone-200 px-6 py-6 flex flex-col gap-5"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="text-[13px] tracking-[0.2em] uppercase text-stone-800"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="btn-primary text-[11px] mt-2 justify-center"
          >
            Request Consultation
          </a>
        </div>
      )}
    </header>
  );
}
